/**
 * 深色商务模板 - 首页
 * 
 * 设计风格（暗黑奢华风，与 Classic 暖色 / Modern 蓝色 / Green 自然风完全不同）：
 * - 深色渐变 Hero 区 + 几何线条装饰
 * - 数据统计条（总服务数/分类数等）
 * - 分类采用横向滚动豪华卡片
 * - 信息列表采用暗色玻璃卡片 + 金色点缀
 * - 奢华高端商务感
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function DarkHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [totalListings, setTotalListings] = useState(0);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 10)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 8 })
      .then((data) => {
        setListings(data.list || []);
        setTotalListings(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />

      {/* ── Hero 暗黑奢华 ── */}
      <section className="relative overflow-hidden bg-gray-950 min-h-[560px] flex items-center">
        {/* 几何装饰背景 */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-900/20 via-gray-900 to-gray-950" />
          {/* 网格线 */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: "linear-gradient(rgba(245,158,11,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px"
          }} />
          {/* 光晕 */}
          <div className="absolute top-20 right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-amber-600/8 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24 text-center">
          {/* 标签 */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-xs font-medium mb-6 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
            高端商务服务平台
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4">
            连接优质
            <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent"> 商业资源</span>
          </h1>
          <p className="text-base md:text-lg text-gray-400 max-w-xl mx-auto mb-10 leading-relaxed">
            汇聚行业精英，打造高端商业服务生态圈
          </p>

          {/* 搜索框 - 暗黑金属风 */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto">
            <div className="relative flex items-center bg-gray-900 border border-gray-700 rounded-2xl overflow-hidden focus-within:border-amber-500/50 focus-within:shadow-lg focus-within:shadow-amber-500/10 transition-all duration-300">
              <i className="ri-search-line pl-5 text-gray-500 text-lg" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索高端商业服务..."
                className="flex-1 py-4 px-3 text-sm md:text-base text-gray-200 placeholder-gray-500 bg-transparent focus:outline-none"
              />
              <button
                type="submit"
                className="m-1.5 px-7 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 text-gray-900 font-bold text-sm rounded-xl hover:from-amber-400 hover:to-yellow-500 hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                探索
              </button>
            </div>
            {showSearchTip && (
              <div className="mt-3 px-4 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm rounded-xl inline-block backdrop-blur-sm">
                <i className="ri-error-warning-line mr-1" />{searchConfig.disabledMessage}
              </div>
            )}
          </form>
        </div>

        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent" />
      </section>

      {/* ── 数据统计条 ── */}
      <section className="relative -mt-16 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gray-900 border border-gray-800 rounded-3xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-6 shadow-2xl shadow-black/40 backdrop-blur-sm">
            {[
              { value: totalListings || listings.length, label: "服务项目", icon: "ri-briefcase-line" },
              { value: categories.length, label: "服务分类", icon: "ri-stack-line" },
              { value: "99.9%", label: "在线率", icon: "ri-shield-check-line" },
              { value: "24h", label: "响应时间", icon: "ri-timer-flash-line" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/20 to-yellow-600/10 text-amber-400">
                  <i className={`${stat.icon} text-lg`} />
                </div>
                <p className="text-2xl md:text-3xl font-extrabold text-white">{stat.value}</p>
                <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 服务分类 ── 横向滚动豪华卡片 */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-full uppercase tracking-wider border border-amber-500/20 mb-3">SERVICE CATEGORIES</span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white">专业服务领域</h2>
            <p className="text-sm text-gray-500 mt-2">精选优质商业服务分类</p>
          </div>

          {loadingCategories ? (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {[...Array(5)].map(i => (
                <div key={i} className="animate-pulse shrink-0 w-40 h-44 bg-gray-800 rounded-2xl" />
              ))}
            </div>
          ) : categories.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
              {categories.map((cat, index) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group shrink-0 w-36 md:w-44 snap-start"
                >
                  <div className="relative bg-gray-900 border border-gray-800 rounded-2xl p-5 flex flex-col items-center text-center hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/5 transition-all duration-500 hover:-translate-y-1">
                    {/* 顶部光泽线 */}
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/20 to-yellow-600/10 text-amber-400 group-hover:from-amber-500 group-hover:to-yellow-600 group-hover:text-gray-900 transition-all duration-500 text-xl shadow-lg shadow-transparent group-hover:shadow-amber-500/20">
                      {cat.icon ? <i className={`${cat.icon}`} /> : <i className="ri-vip-crown-line" />}
                    </div>
                    <span className="mt-3 text-sm font-semibold text-gray-300 group-hover:text-amber-400 transition-colors line-clamp-1">{cat.name}</span>
                    <span className="text-xs text-gray-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">探索 →</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* ── 最新发布 ── 暗色玻璃卡片 */}
      <section className="py-16 md:py-20 bg-gray-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="inline-block px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium rounded-full uppercase tracking-wider border border-amber-500/20 mb-2">LATEST</span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white">最新发布</h2>
            </div>
            <Link to="/category/all" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors">
              浏览全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(4)].map(i => (
                <div key={i} className="animate-pulse bg-gray-800 rounded-2xl p-5 border border-gray-700/50">
                  <div className="flex gap-4">
                    <div className="w-24 h-24 bg-gray-700 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-2.5">
                      <div className="w-3/4 h-4 bg-gray-700 rounded-lg" />
                      <div className="w-1/2 h-3 bg-gray-700 rounded" />
                      <div className="w-full h-2.5 bg-gray-700 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {listings.map((listing, index) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className="group relative bg-gray-900 border border-gray-800 rounded-2xl p-5 hover:border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/5 transition-all duration-500 overflow-hidden"
                >
                  {/* 悬停光效 */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/5 to-amber-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative flex gap-4">
                    {/* 图片 */}
                    <div className="w-24 h-24 md:w-28 md:h-28 shrink-0 rounded-xl overflow-hidden bg-gray-800 relative">
                      {listing.images?.[0] ? (
                        <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-2xl text-gray-600" />
                        </div>
                      )}
                      {listing.is_pinned && (
                        <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-amber-500 text-gray-900 text-xs font-bold rounded-md">
                          置顶
                        </span>
                      )}
                    </div>

                    {/* 内容 */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <h3 className="font-bold text-gray-200 text-sm md:text-base line-clamp-1 group-hover:text-amber-400 transition-colors">
                          {listing.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-xs rounded-lg font-medium">
                            {listing.category?.name || '未分类'}
                          </span>
                          <span className="text-xs text-gray-600">{formatRelativeTime(listing.created_at)}</span>
                        </div>
                        <p className="text-gray-500 text-xs mt-2 line-clamp-2 leading-relaxed">{listing.content}</p>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-800">
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <i className="ri-user-line" />
                          <span>{listing.contact_name || '匿名'}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <i className="ri-eye-line" />
                          <span>{listing.view_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-900 rounded-2xl border border-dashed border-gray-800">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-gray-800">
                <i className="ri-inbox-line text-2xl text-gray-600" />
              </div>
              <p className="text-gray-500">暂无信息</p>
            </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Link to="/category/all" className="inline-flex items-center gap-1 text-sm font-semibold text-amber-400 hover:text-amber-300">
              浏览全部发布 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900 to-gray-950" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-20 w-48 h-48 bg-yellow-600 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">开启您的商业新篇章</h2>
          <p className="text-gray-400 mb-8">加入高端商务平台，拓展您的商业版图</p>
          <button
            onClick={() => navigate("/publish")}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-500 to-yellow-600 text-gray-900 font-bold rounded-2xl hover:from-amber-400 hover:to-yellow-500 hover:shadow-xl hover:shadow-amber-500/25 transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
          >
            <i className="ri-add-circle-line" />
            立即入驻
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "昨天";
  if (days < 7) return `${days}天前`;
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}
